import classNames from "classnames";
import { t } from "i18next";
import React, { useMemo, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";

import type { ICaaCustomerAddress } from "@repo/shopify/queries/customer-account-gql";
import useCustomerQueryCaa from "@repo/shopify/queries/useCustomerQueryCaa";

import Button from "@atoms/Button";
import Input from "@atoms/Input";

import { NOTIFICATIONS } from "@const/notifications";

import apolloCaaClient from "@lib/apolloCaa";

import { useLayoutStore } from "@store/layout";

interface IProps {
  address?: ICaaCustomerAddress;
  isDefaultAddress?: boolean;
  isEmptyAddress?: boolean;
  className?: string;
  onClickCancel?: () => void;
  onCompletedMutation?: () => void | Promise<void>;
}

interface IFormAddress {
  lastName: string;
  firstName: string;
  phoneNumber: string;
  zip: string;
  zoneCode: string;
  city: string;
  address1: string;
  address2: string;
  company: string;
}

export default function AddressFormJp({
  address,
  isDefaultAddress = false,
  isEmptyAddress = false,
  className,
  onClickCancel,
  onCompletedMutation,
}: IProps) {
  const { createCaaAddress, createCaaAddressLoading, editCaaAddress, editCaaAddressLoading } = useCustomerQueryCaa({
    client: apolloCaaClient,
  });
  const { openToast } = useLayoutStore();

  const isEdit = Boolean(address?.id);

  const [isProgress, setIsProgress] = useState(false);
  const [checkedDefaultAddress, setCheckedDefaultAddress] = useState(false);

  const defaultValues: IFormAddress = useMemo(
    () => ({
      lastName: address?.lastName || "",
      firstName: address?.firstName || "",
      phoneNumber: address?.phoneNumber || "",
      zip: address?.zip || "",
      zoneCode: address?.zoneCode || "",
      city: address?.city || "",
      address1: address?.address1 || "",
      address2: address?.address2 || "",
      company: "",
    }),
    [address],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormAddress>({ defaultValues });

  const onSubmit: SubmitHandler<IFormAddress> = async (formData) => {
    if (isProgress) return;

    setIsProgress(true);

    try {
      const input = { ...formData, territoryCode: "JP" };
      const defaultAddress = (checkedDefaultAddress || isEmptyAddress) ?? false;

      if (isEdit && address?.id) {
        const { data } = await editCaaAddress({ variables: { addressId: address.id, address: input, defaultAddress } });
        if (data?.customerAddressUpdate.userErrors.length) {
          openToast(NOTIFICATIONS.ERROR_SERVER);
          return;
        }
      } else {
        const { data } = await createCaaAddress({ variables: { address: input, defaultAddress } });
        if (data?.customerAddressCreate.userErrors.length) {
          openToast(NOTIFICATIONS.ERROR_SERVER);
          return;
        }
      }

      await onCompletedMutation?.();
      onClickCancel?.();
    } finally {
      setIsProgress(false);
    }
  };

  const isLoading = isProgress || createCaaAddressLoading || editCaaAddressLoading;

  return (
    <form className="flex h-[calc(100dvh-7.5rem)] flex-col" onSubmit={handleSubmit(onSubmit)}>
      <div className={classNames("scrollbar-hide min-h-0 flex-1 content-start items-start overflow-y-scroll", className)}>
        {/* 1. 성 (姓) */}
        <div className="col-span-3">
          <Input
            {...register("lastName", { required: true })}
            errorMessage={errors.lastName ? t(`error.necessary_lastName`) : ""}
            isError={Boolean(errors.lastName)}
            label={t("last_name")}
            placeholder={t("last_name")}
          />
        </div>
        {/* 2. 이름 (名) */}
        <div className="col-span-3">
          <Input
            {...register("firstName", { required: true })}
            errorMessage={errors.firstName ? t(`error.necessary_firstName`) : ""}
            isError={Boolean(errors.firstName)}
            label={t("first_name")}
            placeholder={t("first_name")}
          />
        </div>

        {/* 3. 우편번호 (郵便番号) */}
        <div className="col-span-3">
          <Input
            {...register("zip", { required: true })}
            errorMessage={errors.zip ? t(`error.necessary_zip`) : ""}
            isError={Boolean(errors.zip)}
            label={t("postal_code")}
            placeholder="-"
          />
        </div>

        {/* 4. 도도부현 (都道府県) */}
        <div className="col-span-3">
          <Input
            {...register("zoneCode", { required: true })}
            errorMessage={errors.zoneCode ? t(`error.necessary_province`) : ""}
            isError={Boolean(errors.zoneCode)}
            label={t("province")}
            placeholder={t("province")}
          />
        </div>

        {/* 5. 시구정촌 (市区町村) */}
        <div className="col-span-6">
          <Input
            {...register("city", { required: true })}
            errorMessage={errors.city ? t(`error.necessary_city`) : ""}
            isError={Boolean(errors.city)}
            label="市区町村"
            placeholder="市区町村"
          />
        </div>

        {/* 6. 주소 (住所) */}
        <div className="col-span-6">
          <Input
            {...register("address1", { required: true })}
            errorMessage={errors.address1 ? t(`error.necessary_address1`) : ""}
            isError={Boolean(errors.address1)}
            label={t("address1")}
            placeholder={t("address1")}
          />
        </div>

        {/* 7. 건물명, 방 번호 등 (建物名、部屋番号など) */}
        <div className="col-span-6">
          <Input {...register("address2")} label={t("address2")} placeholder={t("address2")} />
        </div>

        {/* 8. 전화번호 (携帯電話) */}
        <div className="col-span-6">
          <Input
            {...register("phoneNumber", { required: true })}
            errorMessage={errors.phoneNumber ? t(`error.necessary_phone`) : ""}
            isError={Boolean(errors.phoneNumber)}
            label={t("phone_number")}
            placeholder={t("phone_number")}
            type="tel"
          />
        </div>
      </div>

      <div className="pc:gap-2.5 pc:pt-4 col-span-8 flex w-full shrink-0 cursor-pointer flex-col gap-5 pt-5">
        {!isDefaultAddress && !isEmptyAddress && (
          <Input
            checked={checkedDefaultAddress}
            id="defaultAddress"
            label={t("addresses.set_as_default_address")}
            name="defaultAddress"
            onChange={() => {
              setCheckedDefaultAddress((prev) => !prev);
            }}
            type="checkbox"
          />
        )}
        <Button as="square" disabled={isLoading} loading={isLoading} type="submit">
          {t("addresses.save")}
        </Button>
      </div>
    </form>
  );
}
